/**
 * Dashboard Page
 *
 * Main admin dashboard with statistics, recent jobs, and quick actions.
 *
 * @package MultilingualPressZone
 * @since 1.0.0
 */

import { el, __, formatDate, formatNumber, clear } from '../utils/dom.js';
import api from '../utils/api.js';
import logger from '../utils/logger.js';
import { Card } from '../components/Card.js';
import Table from '../components/Table.js';
import { Button } from '../components/Button.js';
import StatCard from '../components/StatCard.js';
import { PageHeader } from '../components/PageHeader.js';
import AnalyticsDashboard from '../sections/analytics-dashboard.js';

/**
 * Dashboard Page Class
 */
export default class DashboardPage {
    /**
     * Constructor
     *
     * @param {HTMLElement} container - Container element
     */
    constructor(container) {
        this.container = container;
        this.stats = null;
        this.recentActivity = null;
        this.statsGrid = null;
        this.activityTableContainer = null;
        this.statusBadges = {};
        this.refreshInterval = null;

        this.analytics = null;
        this.analyticsRoot = null;
    }

    /**
     * Get the stat card definitions used by the dashboard.
     *
     * @return {Array<{icon: string, label: string, key: string, formatter: Function}>}
     */
    getStatCards() {
        return [
            {
                icon: '🌐',
                label: __('Total Languages', 'multilingual-press-zone'),
                key: 'total_languages',
                formatter: (value) => formatNumber(value)
            },
            {
                icon: '📝',
                label: __('Active Translations', 'multilingual-press-zone'),
                key: 'active_translations',
                formatter: (value) => formatNumber(value)
            },
            {
                icon: '✅',
                label: __('Completion Rate', 'multilingual-press-zone'),
                key: 'completion_rate',
                formatter: (value) => `${value}%`
            }
        ];
    }

    /**
     * Normalize the dashboard stats payload.
     *
     * @param {Object} payload - Raw API payload
     * @return {Object} Normalized stats object
     */
    normalizeStats(payload = {}) {
        const readNumber = (snakeKey, camelKey) => {
            const value = payload?.[snakeKey] ?? payload?.[camelKey] ?? 0;
            const parsed = typeof value === 'string' ? parseFloat(value) : value;

            return Number.isFinite(parsed) ? parsed : 0;
        };

        return {
            total_languages: readNumber('total_languages', 'totalLanguages'),
            active_translations: readNumber('active_translations', 'activeTranslations'),
            completion_rate: readNumber('completion_rate', 'completionRate'),
            cache_enabled: Boolean(payload?.cache_enabled ?? payload?.cacheEnabled),
            license_valid: Boolean(payload?.license_valid ?? payload?.licenseValid)
        };
    }

    /**
     * Render the current set of stat cards.
     *
     * @param {Object|null} stats - Stats object
     * @param {boolean} isError - Whether to render error state
     * @param {boolean} isLoading - Whether to render loading state
     */
    renderStatsCards(stats = null, isError = false, isLoading = false) {
        if (!this.statsGrid) {
            return;
        }

        clear(this.statsGrid);

        this.getStatCards().forEach((card) => {
            this.statsGrid.appendChild(StatCard({
                icon: card.icon,
                label: card.label,
                value: isLoading
                    ? __('Loading...', 'multilingual-press-zone')
                    : isError
                    ? __('Error', 'multilingual-press-zone')
                    : card.formatter(stats?.[card.key] ?? 0),
                variant: isError ? 'error' : 'primary'
            }));
        });
    }

    /**
     * Render the dashboard
     */
    render() {
        logger.info('Rendering dashboard page');

        // Clear container
        clear(this.container);
        this.container.className = 'presszone-multilingual-dashboard';

        this.container.appendChild(this.renderHeader());
        this.container.appendChild(this.renderStatsSection());
        this.container.appendChild(this.renderAnalyticsSection());
        this.container.appendChild(this.renderBottomGrid());

        // Load data
        this.loadStats();
        this.loadRecentActivity();

        // Load analytics data (non-fatal)
        if (this.analytics) {
            this.analytics.render().catch((e) => {
                logger.error('Analytics section failed to render', e);
            });
        }

        // Setup auto-refresh for jobs
        this.setupAutoRefresh();
    }

    /**
     * Render merged analytics section (from the former /#/analytics route).
     */
    renderAnalyticsSection() {
        const section = el('section', {
            class: 'presszone-multilingual-dashboard__analytics',
            id: 'mpz-dashboard-analytics'
        });

        this.analyticsRoot = el('div', { id: 'mpz-dashboard-analytics-root' });
        section.appendChild(this.analyticsRoot);

        this.analytics = new AnalyticsDashboard(this.analyticsRoot, {
            layout: 'dashboard',
            showHeader: false
        });

        return section;
    }

    /**
     * Render page header
     *
     * @return {HTMLElement} Header element
     */
    renderHeader() {
        return PageHeader({
            title: __('Dashboard', 'multilingual-press-zone'),
            description: __('Translation statistics and recent activity', 'multilingual-press-zone'),
            variant: 'settings'
        });
    }

    /**
     * Render stats cards section
     *
     * @return {HTMLElement} Stats section
     */
    renderStatsSection() {
        this.statsGrid = el('div', { class: 'presszone-multilingual-dashboard__stats-grid' });
        this.renderStatsCards(null, false, true);

        return this.statsGrid;
    }

    /**
     * Render the lower grid: jobs, actions, status.
     */
    renderBottomGrid() {
        const bottom = el('div', { class: 'presszone-multilingual-dashboard__bottom-grid' });

        // Recent activity card
        this.activityTableContainer = el('div', { class: 'mpz-dashboard__table-container' });
        bottom.appendChild(
            Card('\uD83D\uDD58', __('Recent Activity', 'multilingual-press-zone'), __('Latest translations', 'multilingual-press-zone'), [
                this.activityTableContainer
            ])
        );

        // Quick actions card
        bottom.appendChild(
            Card('⚡', __('Quick Actions', 'multilingual-press-zone'), __('Jump to common tasks', 'multilingual-press-zone'), [
                el('div', { class: 'mpz-dashboard__action-buttons' },
                    Button({
                        label: __('Add Language', 'multilingual-press-zone'),
                        variant: 'primary',
                        icon: '+',
                        onClick: () => this.handleAddLanguage()
                    }),
                    Button({
                        label: __('Create Translation', 'multilingual-press-zone'),
                        variant: 'secondary',
                        icon: '🌐',
                        onClick: () => this.handleCreateTranslation()
                    }),
                    Button({
                        label: __('View Settings', 'multilingual-press-zone'),
                        variant: 'ghost',
                        icon: '⚙️',
                        onClick: () => this.handleViewSettings()
                    })
                )
            ])
        );

        // System status card
        bottom.appendChild(
            Card('🩺', __('System Status', 'multilingual-press-zone'), __('Connectivity and licensing health', 'multilingual-press-zone'), [
                el('div', { class: 'mpz-dashboard__status-items' },
                    this.renderStatusItem('api', __('API Connection', 'multilingual-press-zone'), 'checking'),
                    this.renderStatusItem('cache', __('Cache Status', 'multilingual-press-zone'), 'checking'),
                    this.renderStatusItem('license', __('License Status', 'multilingual-press-zone'), 'checking')
                )
            ])
        );

        return bottom;
    }

    /**
     * Render a single stat card
     *
     * @param {string} id - Card ID
     * @param {string} value - Stat value
     * @param {string} label - Stat label
     * @param {string} icon - Dashicon class
     * @return {HTMLElement} Stat card
     */
    renderStatCard(id, value, label, icon) {
        const card = el('div', {
            class: 'mpz-stat-card',
            'data-stat-id': id
        },
            el('div', { class: 'mpz-stat-card__icon' },
                el('span', { class: `dashicons ${icon}`, 'aria-hidden': 'true' })
            ),
            el('div', { class: 'mpz-stat-card__content' },
                el('div', { class: 'mpz-stat-card__value' }, value),
                el('div', { class: 'mpz-stat-card__label' }, label)
            )
        );

        return card;
    }

    /**
     * Update a stat card with new data
     *
     * @param {string} id - Card ID
     * @param {string} value - New value
     */
    updateStatCard(id, value) {
        const card = this.container.querySelector(`[data-stat-id="${id}"]`);
        if (card) {
            const valueEl = card.querySelector('.mpz-stat-card__value');
            if (valueEl) {
                valueEl.textContent = value;
            }
        }
    }

    /**
     * Render actions and system status section
     *
     * @return {HTMLElement} Actions section
     */
    renderActionsSection() {
        const section = el('div', { class: 'mpz-dashboard__actions-wrapper' });

        // Quick actions card
        const actionsCard = el('div', { class: 'mpz-dashboard__actions' },
            el('h3', {}, __('Quick Actions', 'multilingual-press-zone')),
            el('div', { class: 'mpz-dashboard__action-buttons' },
                Button({
                    label: __('Add Language', 'multilingual-press-zone'),
                    variant: 'primary',
                    icon: '\u002B',
                    onClick: () => this.handleAddLanguage()
                }),
                Button({
                    label: __('Create Translation', 'multilingual-press-zone'),
                    variant: 'secondary',
                    icon: '\uD83C\uDF10',
                    onClick: () => this.handleCreateTranslation()
                }),
                Button({
                    label: __('View Settings', 'multilingual-press-zone'),
                    variant: 'ghost',
                    icon: '\u2699\uFE0F',
                    onClick: () => this.handleViewSettings()
                })
            )
        );

        // System status card
        const statusCard = el('div', { class: 'mpz-dashboard__system-status' },
            el('h3', {}, __('System Status', 'multilingual-press-zone')),
            el('div', { class: 'mpz-dashboard__status-items' },
                this.renderStatusItem('api', __('API Connection', 'multilingual-press-zone'), 'checking'),
                this.renderStatusItem('cache', __('Cache Status', 'multilingual-press-zone'), 'checking'),
                this.renderStatusItem('license', __('License Status', 'multilingual-press-zone'), 'checking')
            )
        );

        section.appendChild(actionsCard);
        section.appendChild(statusCard);

        return section;
    }

    /**
     * Render a system status item
     *
     * @param {string} id - Status ID
     * @param {string} label - Status label
     * @param {string} status - Status: 'active'|'error'|'checking'
     * @return {HTMLElement} Status item
     */
    renderStatusItem(id, label, status = 'checking') {
        const badge = el('span', { class: this.getStatusBadgeClass(status) }, this.getStatusText(status));
        this.statusBadges[id] = badge;

        return el('div', {
            class: 'mpz-dashboard__status-item',
            'data-status-id': id
        },
            el('span', { class: 'mpz-dashboard__status-label' }, label),
            badge
        );
    }

    /**
     * Get status text
     *
     * @param {string} status - Status code
     * @return {string} Status text
     */
    getStatusText(status) {
        const statusMap = {
            active: __('Active', 'multilingual-press-zone'),
            error: __('Error', 'multilingual-press-zone'),
            checking: __('Checking...', 'multilingual-press-zone')
        };
        return statusMap[status] || statusMap.checking;
    }

    /**
     * Get status variant
     *
     * @param {string} status - Status code
     * @return {string} Badge variant
     */
    getStatusBadgeClass(status) {
        const base = 'presszone-multilingual-badge';
        const variantMap = {
            active: 'success',
            error: 'danger',
            checking: 'warning'
        };
        const v = variantMap[status] || 'warning';
        return `${base} ${base}--${v}`;
    }

    /**
     * Update system status item
     *
     * @param {string} id - Status ID
     * @param {string} status - New status
     */
    updateStatusItem(id, status) {
        const badge = this.statusBadges[id];
        if (!badge) return;
        badge.textContent = this.getStatusText(status);
        badge.className = this.getStatusBadgeClass(status);
    }

    /**
     * Load dashboard statistics
     */
    async loadStats() {
        logger.info('Loading dashboard statistics');

        try {
            const response = await api.get('/dashboard/stats');
            this.stats = this.normalizeStats(response.data || response);
            this.renderStatsCards(this.stats);

            // Update system status
            this.updateStatusItem('api', 'active');
            this.updateStatusItem('cache', this.stats.cache_enabled ? 'active' : 'error');
            this.updateStatusItem('license', this.stats.license_valid ? 'active' : 'error');

        } catch (error) {
            logger.error('Failed to load dashboard stats:', error);
            this.renderStatsCards(null, true);

            // Update system status
            this.updateStatusItem('api', 'error');
            this.updateStatusItem('cache', 'error');
            this.updateStatusItem('license', 'error');
        }
    }

    /**
     * Load recent translation activity
     */
    async loadRecentActivity() {
        logger.info('Loading recent translation activity');

        const tableContainer = this.activityTableContainer;
        if (!tableContainer) return;

        // Show loading state
        clear(tableContainer);
        const loadingEl = el('div', { class: 'presszone-multilingual-loading' },
            el('div', { class: 'presszone-multilingual-spinner' }),
            el('p', {}, __('Loading recent activity...', 'multilingual-press-zone'))
        );
        tableContainer.appendChild(loadingEl);

        try {
            const response = await api.get('/analytics/recent-activity');
            this.recentActivity = response.data || response;

            // Clear loading
            clear(tableContainer);

            // Create table
            const table = new Table({
                columns: [
                    { label: __('Title', 'multilingual-press-zone'), key: 'title', width: '40%' },
                    { label: __('Type', 'multilingual-press-zone'), key: 'type', width: '15%' },
                    { label: __('Language', 'multilingual-press-zone'), key: 'language', width: '15%' },
                    { label: __('Status', 'multilingual-press-zone'), key: 'status', width: '15%' },
                    { label: __('Date', 'multilingual-press-zone'), key: 'date', width: '15%' }
                ],
                emptyMessage: __('No recent translation activity.', 'multilingual-press-zone')
            });

            const tableElement = table.render(this.recentActivity, (item) => this.renderActivityRow(item));
            tableContainer.appendChild(tableElement);

        } catch (error) {
            logger.error('Failed to load recent activity:', error);

            // Show error message
            clear(tableContainer);
            const errorEl = el('div', { class: 'mpz-dashboard__error' },
                el('p', {}, __('Failed to load recent activity. Please try again later.', 'multilingual-press-zone'))
            );
            tableContainer.appendChild(errorEl);
        }
    }

    /**
     * Render an activity table row
     *
     * @param {Object} item - Activity item data
     * @return {HTMLElement} Table row
     */
    renderActivityRow(item) {
        const statusInfo = this.getStatusInfo(item.status);
        const statusBadge = el('span', { class: `presszone-multilingual-badge presszone-multilingual-badge--${statusInfo.variant}` }, statusInfo.text);
        const typeLabel = item.type === 'content' ? __('Content', 'multilingual-press-zone') : __('String', 'multilingual-press-zone');
        const typeBadge = el('span', { class: 'presszone-multilingual-badge presszone-multilingual-badge--primary' }, typeLabel);

        return el('tr', {},
            el('td', {}, el('span', { class: 'mpz-dashboard__activity-title' }, String(item.title || __('Untitled', 'multilingual-press-zone')))),
            el('td', {}, typeBadge),
            el('td', {}, item.language_code ? item.language_code.toUpperCase() : '-'),
            el('td', {}, statusBadge),
            el('td', {}, formatDate(item.updated_at))
        );
    }

    /**
     * Map translation status codes to a badge variant.
     */
    getStatusInfo(status) {
        const s = String(status || '').toLowerCase();

        if (s === 'translated') {
            return { text: __('Translated', 'multilingual-press-zone'), variant: 'success' };
        }
        if (s === 'needs_update') {
            return { text: __('Needs Update', 'multilingual-press-zone'), variant: 'warning' };
        }
        if (s === 'draft') {
            return { text: __('Draft', 'multilingual-press-zone'), variant: 'primary' };
        }
        if (s === 'pending' || s === 'needs_review') {
            return { text: __('Pending', 'multilingual-press-zone'), variant: 'warning' };
        }

        return { text: status ? String(status) : __('Unknown', 'multilingual-press-zone'), variant: 'primary' };
    }

    /**
     * Setup auto-refresh for jobs
     */
    setupAutoRefresh() {
        // Refresh activity every 60 seconds
        this.refreshInterval = setInterval(() => {
            this.loadRecentActivity();
        }, 60000);
    }

    /**
     * Handle add language action
     */
    handleAddLanguage() {
        logger.info('Add language clicked');
        // Navigate to languages page (will be implemented in routing)
        window.location.hash = '#/languages/add';
    }

    /**
     * Handle create translation action
     */
    handleCreateTranslation() {
        logger.info('Create translation clicked');
        // Navigate to translations page (will be implemented in routing)
        window.location.hash = '#/translations/create';
    }

    /**
     * Handle view settings action
     */
    handleViewSettings() {
        logger.info('View settings clicked');
        window.location.hash = '#/licensing';
    }

    /**
     * Destroy the dashboard
     * Clean up event listeners and intervals
     */
    destroy() {
        logger.info('Destroying dashboard page');

        // Clear auto-refresh interval
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }

        if (this.analytics && typeof this.analytics.destroy === 'function') {
            this.analytics.destroy();
        }
        this.analytics = null;
        this.analyticsRoot = null;

        // Clear container
        if (this.container) {
            clear(this.container);
        }

        // Clear references
        this.stats = null;
        this.recentActivity = null;
        this.statsGrid = null;
        this.activityTableContainer = null;
        this.statusBadges = {};
    }
}
